//React
import React, { createContext, useContext, useId } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
//CSS
import "./inputs.scss";

const Scope = createContext(null);

/**
 * One react-tooltip shared by every HoverTip underneath (ASB-4377): a table
 * or a card renders a single `<Tooltip>` instead of one per cell. A HoverTip
 * outside any scope renders its own.
 */
export function HoverTipScope({ id, placement, children }) {
	const autoId = useId();
	const tipId = id ?? `hovertip-${autoId}`;
	return (
		<Scope.Provider value={tipId}>
			{children}
			<Tooltip
				id={tipId}
				place={placement ?? "top"}
				className="as-hovertip__tip"
			/>
		</Scope.Provider>
	);
}

HoverTipScope.propTypes = {
	/** override the generated tooltip id (needed when an ancestor anchors it, see hoverTipAnchor) */
	id: PropTypes.string,
	placement: PropTypes.oneOf(["top", "right", "bottom", "left"]),
	children: PropTypes.node,
};

/**
 * The `data-tooltip-*` and `aria-description` attributes that make an element
 * the tooltip's anchor, for the case where the label's own focusable ancestor
 * (a sortable column header) must show the tip: the HoverTip inside it takes
 * `trigger="ancestor"` and the ancestor spreads these.
 */
export const hoverTipAnchor = ({ id, content }) => ({
	"data-tooltip-id": id,
	"data-tooltip-content": content,
	"aria-description": content,
});

/**
 * Help on hover over the text itself (ASB-4377), in place of an info icon
 * beside it: the admin dashboards show the same help on ninety-odd labels,
 * and an icon on each read as noise. A label read once (a column header, a
 * KPI label, a legend entry) gets a dotted underline that says it has help; a
 * value repeated down a column (`underline={false}`) shows the tip without
 * one. The label is a tab stop and the tip shows on focus; the help is also
 * the label's accessible description (`aria-description`, an attribute
 * rather than a hidden copy of the text, so a table's cells copy and read as
 * their values alone), so what the icon's label carried is not lost. A tap
 * shows it too: react-tooltip opens on mouseover, which touch synthesises.
 */
function HoverTip({ content, children, underline, placement, focusable, trigger, className }) {
	const autoId = useId();
	const shared = useContext(Scope);
	const tipId = shared ?? `hovertip-${autoId}`;
	const self = (trigger ?? "self") === "self";
	return (
		<>
			<span
				className={["as-hovertip", underline === false ? "as-hovertip--plain" : "", className ?? ""]
					.join(" ")
					.trim()}
				data-tooltip-id={self ? tipId : undefined}
				data-tooltip-content={self ? content : undefined}
				tabIndex={self && focusable !== false ? 0 : undefined}
				aria-description={self ? content : undefined}
			>
				{children}
			</span>
			{shared || !self ? null : (
				<Tooltip
					id={tipId}
					place={placement ?? "top"}
					className="as-hovertip__tip"
				/>
			)}
		</>
	);
}

HoverTip.propTypes = {
	/** the tooltip text */
	content: PropTypes.string.isRequired,
	/** the label */
	children: PropTypes.node.isRequired,
	/** dotted underline under the label; default true. Off for a value repeated down a column. */
	underline: PropTypes.bool,
	/** ignored inside a HoverTipScope, which places every tip */
	placement: PropTypes.oneOf(["top", "right", "bottom", "left"]),
	/** the label is a tab stop; default true. Off inside a button, which must not nest one. */
	focusable: PropTypes.bool,
	/**
	 * "self" (default): the label shows the tip on hover and focus. "ancestor":
	 * an ancestor spreading `hoverTipAnchor` does, and the label only looks the
	 * part — a sortable header cell, whose focus and click already belong to
	 * the sort.
	 */
	trigger: PropTypes.oneOf(["self", "ancestor"]),
	className: PropTypes.string,
};

export default HoverTip;
